// Admin Shared Components
import React, { useState, useRef } from 'react';
import { X, Loader2, Upload, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { compressImage } from '../../utils/imageCompression';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ============== REUSABLE IMAGE UPLOAD FIELD ==============
export const ImageUploadField = ({ value, onChange, label = "Image", showPreview = true }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      // Compress image before upload
      const compressedFile = await compressImage(file, { maxWidth: 1200, quality: 0.85 });
      const formData = new FormData();
      formData.append('file', compressedFile);

      const response = await fetch(`${BACKEND_URL}/api/admin/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        onChange(`${BACKEND_URL}${data.url}`);
      }
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      
      {/* Image preview */}
      {showPreview && value && (
        <div className="relative w-full h-32 bg-slate-100 rounded-lg overflow-hidden mb-2">
          <img src={value} alt="Preview" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      
      {/* Upload controls */}
      <div className="flex gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
          className="hidden"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex-shrink-0"
        >
          {uploading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Upload...</>
          ) : (
            <><Upload className="w-4 h-4 mr-2" /> Fichier</>
          )}
        </Button>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="ou coller une URL..."
          className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
        />
      </div>
    </div>
  );
};

// ============== PARTNER FORM ==============
export const PartnerForm = ({ form, setForm, onSave, onCancel }) => {
  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleMultipleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    const newImages = [...(form.images || [])];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(`${BACKEND_URL}/api/admin/upload`, { method: 'POST', credentials: 'include', body: formData });
        if (response.ok) {
          const data = await response.json();
          newImages.push(`${BACKEND_URL}${data.url}`);
        }
      } catch (err) { console.error('Upload error:', err); }
    }
    setForm({...form, images: newImages});
    setUploading(false);
  };

  const removeImage = (index) => {
    setForm({...form, images: (form.images || []).filter((_, i) => i !== index)});
  };

  return (
    <div className="space-y-6">
      {/* Basic Info */}
      <div className="border border-slate-200 rounded-lg p-4">
        <h4 className="font-medium text-slate-800 mb-4">Informations de base</h4>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nom</label>
            <input type="text" value={form.name || ''} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Pays</label>
            <input type="text" value={form.country || ''} onChange={(e) => setForm({...form, country: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <ImageUploadField value={form.logo} onChange={(url) => setForm({...form, logo: url})} label="Logo" />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Site Web</label>
            <input type="text" value={form.website || ''} onChange={(e) => setForm({...form, website: e.target.value})} placeholder="https://..." className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>
        </div>
      </div>

      {/* Short Description */}
      <div className="border border-slate-200 rounded-lg p-4">
        <h4 className="font-medium text-slate-800 mb-4">Description courte (affichée sur la liste des partenaires)</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">🇬🇧 Description EN</label>
            <textarea value={form.description_en || ''} onChange={(e) => setForm({...form, description_en: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" rows={3} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">🇫🇷 Description FR</label>
            <textarea value={form.description_fr || ''} onChange={(e) => setForm({...form, description_fr: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" rows={3} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">🇩🇿 الوصف AR</label>
            <textarea value={form.description_ar || ''} onChange={(e) => setForm({...form, description_ar: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-right" dir="rtl" rows={3} />
          </div>
        </div>
      </div>

      {/* Full Description */}
      <div className="border border-slate-200 rounded-lg p-4">
        <h4 className="font-medium text-slate-800 mb-4">Description complète (affichée sur la page du partenaire)</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">🇬🇧 Description Complète EN</label>
            <textarea value={form.full_description_en || ''} onChange={(e) => setForm({...form, full_description_en: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" rows={6} placeholder="Description détaillée en anglais..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">🇫🇷 Description Complète FR</label>
            <textarea value={form.full_description_fr || ''} onChange={(e) => setForm({...form, full_description_fr: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" rows={6} placeholder="Description détaillée en français..." />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">🇩🇿 الوصف الكامل AR</label>
            <textarea value={form.full_description_ar || ''} onChange={(e) => setForm({...form, full_description_ar: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-right" dir="rtl" rows={6} placeholder="الوصف التفصيلي بالعربية..." />
          </div>
        </div>
      </div>

      {/* Gallery Images */}
      <div className="border border-slate-200 rounded-lg p-4">
        <h4 className="font-medium text-slate-800 mb-4">Galerie d'images</h4>
        {form.images && form.images.length > 0 && (
          <div className="grid grid-cols-4 gap-3 mb-4">
            {form.images.map((img, i) => (
              <div key={i} className="relative group aspect-video bg-slate-100 rounded-lg overflow-hidden">
                <img src={img} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleMultipleUpload} className="hidden" />
          <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Upload...</> : <><Upload className="w-4 h-4 mr-2" /> Ajouter des images</>}
          </Button>
        </div>
      </div>

      {/* Video */}
      <div className="border border-slate-200 rounded-lg p-4">
        <h4 className="font-medium text-slate-800 mb-4">Vidéo YouTube (optionnel)</h4>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">ID de la vidéo YouTube</label>
          <input type="text" value={form.video_id || ''} onChange={(e) => setForm({...form, video_id: e.target.value})} placeholder="ex: dQw4w9WgXcQ (l'ID après v= dans l'URL)" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          <p className="text-xs text-slate-500 mt-1">Exemple: Pour https://youtube.com/watch?v=<strong>dQw4w9WgXcQ</strong>, entrez: dQw4w9WgXcQ</p>
        </div>
      </div>

      {/* Status */}
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({...form, is_active: e.target.checked})} className="rounded" />
          <span className="text-sm">Actif (visible sur le site)</span>
        </label>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
        <Button variant="ghost" size="sm" onClick={onCancel}><X className="w-4 h-4 mr-1" /> Annuler</Button>
        <Button size="sm" onClick={onSave}><Save className="w-4 h-4 mr-1" /> Enregistrer</Button>
      </div>
    </div>
  );
};

// ============== EVENT FORM ==============
export const EventForm = ({ form, setForm, onSave, onCancel }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-3 gap-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">🇬🇧 Titre EN</label>
        <input
          type="text"
          value={form.title_en || ''}
          onChange={(e) => setForm({...form, title_en: e.target.value})}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">🇫🇷 Titre FR</label>
        <input
          type="text"
          value={form.title_fr || ''}
          onChange={(e) => setForm({...form, title_fr: e.target.value})}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">🇩🇿 العنوان AR</label>
        <input
          type="text"
          value={form.title_ar || ''}
          onChange={(e) => setForm({...form, title_ar: e.target.value})}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-right"
          dir="rtl"
        />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">🇬🇧 Description EN</label>
        <textarea
          value={form.description_en || ''}
          onChange={(e) => setForm({...form, description_en: e.target.value})}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
          rows={3}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">🇫🇷 Description FR</label>
        <textarea
          value={form.description_fr || ''}
          onChange={(e) => setForm({...form, description_fr: e.target.value})}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
          rows={3}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">🇩🇿 الوصف AR</label>
        <textarea
          value={form.description_ar || ''}
          onChange={(e) => setForm({...form, description_ar: e.target.value})}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-right"
          dir="rtl"
          rows={3}
        />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
        <input
          type="date"
          value={form.date || ''}
          onChange={(e) => setForm({...form, date: e.target.value})}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Lieu</label>
        <input
          type="text"
          value={form.location || ''}
          onChange={(e) => setForm({...form, location: e.target.value})}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
          placeholder="Ex: Alger, Palais des Expositions"
        />
      </div>
    </div>
    
    {/* Google Maps Link */}
    <div className="border border-blue-100 bg-blue-50 rounded-lg p-4">
      <label className="block text-sm font-medium text-slate-700 mb-1">📍 Lien Google Maps (optionnel)</label>
      <input
        type="url"
        value={form.maps_url || ''}
        onChange={(e) => setForm({...form, maps_url: e.target.value})}
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
        placeholder="https://maps.google.com/..."
      />
      <div className="mt-2 text-xs text-blue-700 space-y-1">
        <p className="font-medium">Comment obtenir le lien :</p>
        <ol className="list-decimal list-inside space-y-0.5">
          <li>Ouvrez <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Maps</a></li>
          <li>Recherchez le lieu de l'événement</li>
          <li>Cliquez sur <strong>Partager</strong> → <strong>Copier le lien</strong></li>
          <li>Collez le lien ici</li>
        </ol>
      </div>
    </div>
    
    {/* Image upload */}
    <ImageUploadField
      value={form.image_url}
      onChange={(url) => setForm({...form, image_url: url})}
      label="Image de l'événement"
    />
    
    <div className="flex justify-end gap-2">
      <Button variant="ghost" size="sm" onClick={onCancel}>
        <X className="w-4 h-4 mr-1" /> Annuler
      </Button>
      <Button size="sm" onClick={onSave}>
        <Save className="w-4 h-4 mr-1" /> Enregistrer
      </Button>
    </div>
  </div>
);

// ============== CONTACT CARD FORM ==============
export const ContactCardForm = ({ form, setForm, cardTypes, onSave, onCancel }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Type de carte</label>
        <select
          value={form.card_type || 'person'}
          onChange={(e) => setForm({...form, card_type: e.target.value})}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
        >
          {cardTypes.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Ordre d'affichage</label>
        <input
          type="number"
          value={form.order || 0}
          onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
        />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">🇫🇷 Titre FR</label>
        <input
          type="text"
          value={form.title_fr || ''}
          onChange={(e) => setForm({...form, title_fr: e.target.value})}
          placeholder="ex: Gérant, Responsable Commercial..."
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">🇬🇧 Titre EN</label>
        <input
          type="text"
          value={form.title_en || ''}
          onChange={(e) => setForm({...form, title_en: e.target.value})}
          placeholder="ex: Manager, Commercial Manager..."
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">🇩🇿 العنوان AR</label>
        <input
          type="text"
          value={form.title_ar || ''}
          onChange={(e) => setForm({...form, title_ar: e.target.value})}
          placeholder="مثال: المدير، المدير التجاري..."
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-right"
          dir="rtl"
        />
      </div>
    </div>
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">Nom (optionnel)</label>
      <input
        type="text"
        value={form.name || ''}
        onChange={(e) => setForm({...form, name: e.target.value})}
        placeholder="ex: Mr ASSAL Adel"
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
      />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Téléphones (un par ligne)</label>
        <textarea
          value={(form.phones || []).join('\n')}
          onChange={(e) => setForm({...form, phones: e.target.value.split('\n').filter(p => p.trim())})}
          placeholder="+213 770 978 522&#10;+213 561 666 588"
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
          rows={3}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Emails (un par ligne)</label>
        <textarea
          value={(form.emails || []).join('\n')}
          onChange={(e) => setForm({...form, emails: e.target.value.split('\n').filter(em => em.trim())})}
          placeholder="contact@sae-dz.com&#10;info@sae-dz.com"
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
          rows={3}
        />
      </div>
    </div>
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">Adresse (optionnel)</label>
      <textarea
        value={form.address || ''}
        onChange={(e) => setForm({...form, address: e.target.value})}
        placeholder="Zone des siéges, Coopérative Immobilière..."
        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
        rows={2}
      />
    </div>
    <div className="flex items-center gap-4">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={form.is_active !== false}
          onChange={(e) => setForm({...form, is_active: e.target.checked})}
          className="rounded"
        />
        <span className="text-sm">Actif (visible sur le site)</span>
      </label>
    </div>
    <div className="flex justify-end gap-2">
      <Button variant="ghost" size="sm" onClick={onCancel}>
        <X className="w-4 h-4 mr-1" /> Annuler
      </Button>
      <Button size="sm" onClick={onSave}>
        <Save className="w-4 h-4 mr-1" /> Enregistrer
      </Button>
    </div>
  </div>
);

export { BACKEND_URL };
